import { useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { useCanAccess } from '../auth/useCanAccess';
import {
  CustomerHistory,
  RecomputeMode,
  useRecomputeCustomerHistoryMutation,
  useRecomputeDealUsageMutation,
} from './insightRecomputeApi';

type CustomerRecompute = {
  readonly label: string;
  readonly history: CustomerHistory;
  readonly mode: RecomputeMode;
};

const CUSTOMER_RECOMPUTES: CustomerRecompute[] = [
  { label: 'Update renders', history: 'render', mode: 'update' },
  { label: 'Rebuild renders', history: 'render', mode: 'rebuild' },
  { label: 'Update deliveries', history: 'delivery', mode: 'update' },
  { label: 'Rebuild deliveries', history: 'delivery', mode: 'rebuild' },
];

const DEAL_RECOMPUTE_LABEL = 'Recompute deal usage';

const PAGE_WIDTH = { maxWidth: '48rem' };

// The ingress in front of internal-service gives up on a request long before a rebuild finishes,
// and insight carries on regardless, so a gateway timeout is not a failure to retry.
const STILL_RUNNING =
  'no answer before the gateway timed out. Insight is most likely still running it — do not run it again; check the figures in a few minutes.';

type Outcome = {
  readonly succeeded: boolean;
  readonly message: string;
};

type RecomputeResult = { data: void } | { error: FetchBaseQueryError | SerializedError };

const isGatewayTimeout = (error: FetchBaseQueryError | SerializedError): boolean =>
  'status' in error && (error.status === 504 || error.status === 'PARSING_ERROR' || error.status === 'TIMEOUT_ERROR');

const reasonOf = (error: FetchBaseQueryError | SerializedError): string => {
  if (isGatewayTimeout(error)) {
    return STILL_RUNNING;
  }
  const body = 'data' in error ? error.data : undefined;
  if (typeof body === 'object' && body !== null && 'message' in body && typeof body.message === 'string') {
    return body.message;
  }
  return 'status' in error ? `HTTP ${error.status}` : error.message ?? 'unknown error';
};

const InsightRecomputePage = (): JSX.Element => {
  const canRecompute = useCanAccess('admin');
  const [recomputeCustomerHistory] = useRecomputeCustomerHistoryMutation();
  const [recomputeDealUsage] = useRecomputeDealUsageMutation();
  const [customerId, setCustomerId] = useState('');
  const [dealId, setDealId] = useState('');
  const [running, setRunning] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const run = async (label: string, id: string, recompute: () => Promise<RecomputeResult>): Promise<void> => {
    setRunning(label);
    setOutcome(null);

    const result = await recompute();

    setOutcome(
      'error' in result
        ? { succeeded: false, message: `${label} failed for ${id}: ${reasonOf(result.error)}` }
        : { succeeded: true, message: `${label} finished for ${id}` }
    );
    setRunning(null);
  };

  const recomputeButton = (label: string, id: string, danger: boolean, onClick: () => void): JSX.Element => (
    <Button
      key={label}
      className="mr-2 mb-2"
      variant={danger ? 'outline-danger' : 'outline-primary'}
      disabled={!id.trim() || running !== null}
      onClick={onClick}
    >
      {running === label ? `${label}…` : label}
    </Button>
  );

  if (!canRecompute) {
    return <Alert variant="warning">Insight recompute needs the admin tier.</Alert>;
  }

  return (
    <div style={PAGE_WIDTH}>
      <h2>Insight recompute</h2>
      <p className="text-muted">Rebuild the analytics behind Deep Insight and the Custom Deal Tracker.</p>

      {outcome && <Alert variant={outcome.succeeded ? 'success' : 'danger'}>{outcome.message}</Alert>}

      <Card className="mb-4">
        <Card.Header>Customer analytics</Card.Header>
        <Card.Body>
          <p className="text-muted">
            Update walks forward from the account&apos;s last measured day. Rebuild discards what is stored and
            recomputes the whole history from signup, which is slow and hits ClickHouse hard — reach for it only when
            the stored series is wrong, not merely stale.
          </p>
          <Form.Control
            aria-label="Customer id"
            placeholder="Customer id, e.g. 123534"
            value={customerId}
            onChange={(event) => setCustomerId(event.target.value)}
            className="mb-3"
          />
          {CUSTOMER_RECOMPUTES.map(({ label, history, mode }) =>
            recomputeButton(label, customerId, mode === 'rebuild', () =>
              run(label, customerId.trim(), () =>
                recomputeCustomerHistory({ customerId: customerId.trim(), history, mode })
              )
            )
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>Custom deal usage</Card.Header>
        <Card.Body>
          <p className="text-muted">
            Recomputes one deal&apos;s daily render usage by source, which is what the customer sees on the Custom Deal
            Tracker.
          </p>
          <Form.Control
            aria-label="Deal id"
            placeholder="Deal id (uuid)"
            value={dealId}
            onChange={(event) => setDealId(event.target.value)}
            className="mb-3"
          />
          {recomputeButton(DEAL_RECOMPUTE_LABEL, dealId, false, () =>
            run(DEAL_RECOMPUTE_LABEL, dealId.trim(), () => recomputeDealUsage({ dealId: dealId.trim() }))
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default InsightRecomputePage;
